import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ActionButton, Field, SecondaryAction } from "./kit";
import { FailureCard } from "./GoalNotice";
import SettingsSheet from "./SettingsSheet";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";

/**
 * The Coach Portal's "Assign a development goal" dialog, lifted out of
 * `CoachPortal.jsx` for issue #460 (see `CoachGoalsPanel.jsx` for why).
 * Testids, copy and field set are unchanged in the move — `cp-goal-dialog`
 * and `cp-new-goal-btn` are both declared in `e2e/dialog-config.js`.
 */

// #106's phone touch-target idiom, unchanged from `sm:` up.
const TOUCH = "h-11 sm:h-9";

const GOAL_CATEGORIES = ["batting", "bowling", "fielding", "technical", "tactical", "physical", "mental", "general"];

export default function AssignGoalDialog(props) {
  // #1221 slice 2: the coach's player page passes `appearance="r5"` and gets the
  // kit sheet below. Unset (the flag-off page) is the shipped dialog.
  if (props.appearance === "r5") return <AssignGoalSheetR5 {...props} />;
  return <ShippedAssignGoalDialog {...props} />;
}

function ShippedAssignGoalDialog({ open, onOpenChange, playerName, goal, onGoalChange, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cp-goal-dialog">
        <DialogHeader>
          <DialogTitle>Assign a development goal {playerName && `— ${playerName}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Goal title</Label>
            <Input className={TOUCH} value={goal.goal_title} onChange={(e) => onGoalChange({ ...goal, goal_title: e.target.value })} placeholder="e.g. Increase strike rate vs spin" data-testid="cp-goal-title" />
          </div>
          <div>
            <Label>Specific target</Label>
            <Input className={TOUCH} value={goal.target} onChange={(e) => onGoalChange({ ...goal, target: e.target.value })} placeholder="e.g. SR ≥ 120 in T20 innings" data-testid="cp-goal-target" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={goal.description} onChange={(e) => onGoalChange({ ...goal, description: e.target.value })} data-testid="cp-goal-desc" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={goal.category} onValueChange={(v) => onGoalChange({ ...goal, category: v })}>
                <SelectTrigger className={TOUCH} data-testid="cp-goal-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{GOAL_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Horizon</Label>
              <Select value={goal.horizon} onValueChange={(v) => onGoalChange({ ...goal, horizon: v })}>
                <SelectTrigger className={TOUCH} data-testid="cp-goal-horizon"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short-term</SelectItem>
                  <SelectItem value="long">Long-term</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due date</Label>
              <Input className={TOUCH} type="date" value={goal.target_date || ""} onChange={(e) => onGoalChange({ ...goal, target_date: e.target.value })} data-testid="cp-goal-date" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className={TOUCH} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className={TOUCH} onClick={onSave} data-testid="cp-goal-save" style={{ background: "#0B2E59" }}>Assign goal</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// #1221 slice 2 (D240, D251) — `CoachPlayerGoals.dc.html`'s "Assign goal", as a
// kit sheet (the anatomy `NewGoalSheet.jsx` already has: Cancel, then the one
// gold action). The same six fields and the same `POST /goals` body as the
// dialog above. A missing title says so under the field and keeps everything
// else typed (D303: the 2px alert-red border, the sentence stays ink, Save takes
// the coach to the field); a failed save says so in the sheet and keeps
// everything (D57: never a toast). `onSave()` returns a promise that rejects on
// failure and closes the sheet itself on success.
// ---------------------------------------------------------------------------

const SELECT_STYLE = {
  width: "100%",
  height: "var(--nw-size-control)",
  padding: "0 12px",
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-control)",
  fontSize: 15,
  color: "var(--nw-ink)",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
};

const TEXTAREA_STYLE = {
  width: "100%",
  padding: 12,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-control)",
  fontSize: 15,
  lineHeight: 1.45,
  color: "var(--nw-ink)",
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
};

function SheetSelect({ id, label, value, onChange, testId, children }) {
  return (
    <div>
      <label htmlFor={id} className="nw-label" style={{ marginBottom: 4, display: "block" }}>
        {label}
      </label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} style={SELECT_STYLE}>
        {children}
      </select>
    </div>
  );
}

function AssignGoalSheetR5({ open, onOpenChange, playerName, goal, onGoalChange, onSave }) {
  const [titleError, setTitleError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);
  // #1299 (D303): Save scrolls to the first missing field and focuses it.
  const titleRef = useRef(null);
  const [attempt, setAttempt] = useState(0);
  const focusErrors = useMemo(() => ({ title: titleError ? attempt : 0 }), [titleError, attempt]);
  const armFocus = useFocusFirstInvalid(titleRef, focusErrors);

  // A sheet opened again starts without the last one's marks.
  useEffect(() => {
    if (open) {
      setTitleError(false);
      setSaveError(false);
    }
  }, [open]);

  const set = (patch) => onGoalChange({ ...goal, ...patch });

  const assign = async () => {
    if (saving) return;
    armFocus();
    if (!goal.goal_title.trim()) {
      setTitleError(true);
      setAttempt((n) => n + 1);
      return;
    }
    setSaving(true);
    setSaveError(false);
    try {
      await onSave();
    } catch {
      setSaveError(true);
    }
    setSaving(false);
  };

  return (
    <SettingsSheet
      open={open}
      onOpenChange={onOpenChange}
      title={`Assign a development goal${playerName ? ` — ${playerName}` : ""}`}
      data-testid="cp-goal-dialog"
    >
      <div ref={titleRef}>
        <Field
          label="Goal title"
          data-testid="cp-goal-title"
          value={goal.goal_title}
          onChange={(e) => {
            set({ goal_title: e.target.value });
            if (titleError && e.target.value.trim()) setTitleError(false);
          }}
          placeholder="e.g. Increase strike rate vs spin"
          error={titleError ? "Goal title required" : null}
        />
        {titleError && (
          <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Everything else typed is still in the sheet.</div>
        )}
      </div>
      <Field
        label="Specific target"
        data-testid="cp-goal-target"
        value={goal.target}
        onChange={(e) => set({ target: e.target.value })}
        placeholder="e.g. SR ≥ 120 in T20 innings"
      />
      <div>
        <label htmlFor="cp-goal-desc" className="nw-label" style={{ marginBottom: 4, display: "block" }}>
          Description
        </label>
        <textarea
          id="cp-goal-desc"
          data-testid="cp-goal-desc"
          rows={3}
          value={goal.description}
          onChange={(e) => set({ description: e.target.value })}
          className="nw-kit-input"
          style={TEXTAREA_STYLE}
        />
      </div>
      <SheetSelect id="cp-goal-cat" label="Category" value={goal.category} onChange={(v) => set({ category: v })} testId="cp-goal-cat">
        {GOAL_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {c.charAt(0).toUpperCase() + c.slice(1)}
          </option>
        ))}
      </SheetSelect>
      <SheetSelect id="cp-goal-horizon" label="Horizon" value={goal.horizon} onChange={(v) => set({ horizon: v })} testId="cp-goal-horizon">
        <option value="short">Short-term</option>
        <option value="long">Long-term</option>
      </SheetSelect>
      <Field label="Due date" type="date" data-testid="cp-goal-date" value={goal.target_date || ""} onChange={(e) => set({ target_date: e.target.value })} />
      {saveError && <FailureCard data-testid="cp-goal-save-error" title="Failed to create goal" body="Everything you typed is still here. Try again." />}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <SecondaryAction variant="boxed" onClick={() => onOpenChange(false)} disabled={saving} style={{ width: "auto", flexShrink: 0 }}>
          Cancel
        </SecondaryAction>
        <ActionButton
          onClick={assign}
          disabled={saving}
          data-testid="cp-goal-save"
          // The board's greyed primary (#E2E8F0 on #64748B), literal so a unit test can read it.
          style={saving ? { background: "#E2E8F0", color: "#64748B", opacity: 1 } : undefined}
        >
          {saving ? "Assigning…" : "Assign goal"}
        </ActionButton>
      </div>
    </SettingsSheet>
  );
}
